var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../player/DataContext';
import customEventHandler from '../../helper/customEvents';
import ButtonComponent from '../../components/ButtonComponent';
import FooterActionsButtons from './FooterActionsButtons';
import CheckBox from '../../components/CheckBox';
import globalStore from '../../store';
import CustomScrollbar from '../../components/CustomScrollbar';
import { COUNTER_TYPES, MAX_COUNTERS_PER_CONFIG, POPUP_ROW_LAYOUT, COUNTER_TYPE_BY_ID, GAME_MARKER_PALETTE, GAME_MARKER_DEFAULT_COLOR, } from '../../constants/coinsAndCountersConstants';
import { CounterGraphic, hasTwoSides, BEAN_DEFAULT_FRONT_COLOR, BEAN_DEFAULT_BACK_COLOR, } from '../CoinsAndCountersManipulative';
import '../../styles/widgets/coinsAndCounters.scss';
var generateId = function () { return "coinsAndCounters_".concat(uuidv4().replace(/-/g, '')); };
// Forwards clicks on an extended click-target wrapper to the contained
// checkbox input without causing a double-toggle when the input itself is
// clicked (input click already fires onChange → the wrapper's onClick would
// fire again via bubbling). We stop propagation only for the input's own
// subtree; clicks on the label text or padding area are forwarded normally.
var forwardClickToCheckbox = function (e) {
    var _a;
    if (e.target.closest('.checkBtn'))
        return;
    (_a = e.currentTarget.querySelector('input[type="checkbox"]')) === null || _a === void 0 ? void 0 : _a.click();
};
var CoinsAndCountersManipulativePopup = function () {
    var _a, _b, _c, _d;
    var t = useTranslation().t;
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    // C2 — move focus into the dialog when it opens.
    var dialogRef = useRef(null);
    var _e = useContext(DataContext), selectedManipulativeId = _e.selectedManipulativeId, setSelectedManipulativeId = _e.setSelectedManipulativeId, setManiplulatorComponentVisible = _e.setManiplulatorComponentVisible, setActiveManipulator = _e.setActiveManipulator, setSelectedTool = _e.setSelectedTool, manipulative = _e.manipulative, setManipulative = _e.setManipulative, coinsAndCountersDrawer = _e.coinsAndCountersDrawer, setCoinsAndCountersDrawer = _e.setCoinsAndCountersDrawer;
    var _f = useState(new Set()), selected = _f[0], setSelected = _f[1];
    var _g = useState({}), flipMap = _g[0], setFlipMap = _g[1];
    var _h = useState(null), activeType = _h[0], setActiveType = _h[1];
    // Tracks whether the last checkbox toggle was a check (add) or uncheck (remove).
    // The auto-focus effect should only fire on check, not uncheck.
    var lastToggleWasCheckRef = useRef(false);
    var newMarkerState = function () { return ({
        front: GAME_MARKER_DEFAULT_COLOR,
        back: GAME_MARKER_DEFAULT_COLOR,
        side: 'front',
        isFlipping: false,
    }); };
    var _j = useState([
        newMarkerState(),
    ]), markerStates = _j[0], setMarkerStates = _j[1];
    var _k = useState(GAME_MARKER_DEFAULT_COLOR), markerActiveVariant = _k[0], setMarkerActiveVariant = _k[1];
    var _l = useState(false), markerTransparent = _l[0], setMarkerTransparent = _l[1];
    var _m = useState(1), markerCount = _m[0], setMarkerCount = _m[1];
    var _o = useState(null), markerFlipBtnHovered = _o[0], setMarkerFlipBtnHovered = _o[1];
    // Which marker face is the active paint target. Clicking a marker face
    // selects it; swatch clicks apply only to that marker's current visible side.
    var _p = useState(0), selectedMarkerIndex = _p[0], setSelectedMarkerIndex = _p[1];
    // Per-marker flip timers so concurrent flips on different markers don't
    // cancel each other. Each map key is the marker index.
    var markerFlipTimeoutsRef = useRef(new Map());
    var flipMarker = function (i) {
        var existing = markerFlipTimeoutsRef.current.get(i);
        if (existing === null || existing === void 0 ? void 0 : existing.swap)
            clearTimeout(existing.swap);
        if (existing === null || existing === void 0 ? void 0 : existing.end)
            clearTimeout(existing.end);
        setMarkerStates(function (prev) {
            return prev.map(function (m, idx) { return (idx === i ? __assign(__assign({}, m), { isFlipping: false }) : m); });
        });
        requestAnimationFrame(function () {
            setMarkerStates(function (prev) {
                return prev.map(function (m, idx) { return (idx === i ? __assign(__assign({}, m), { isFlipping: true }) : m); });
            });
            // Single 350 ms scaleX cycle: swap side at the 175 ms midpoint,
            // end at 350 ms. Same numbers as the previous single-marker logic.
            var swap = window.setTimeout(function () {
                setMarkerStates(function (prev) {
                    return prev.map(function (m, idx) {
                        return idx === i
                            ? __assign(__assign({}, m), { side: m.side === 'front' ? 'back' : 'front' }) : m;
                    });
                });
            }, 175);
            var end = window.setTimeout(function () {
                setMarkerStates(function (prev) {
                    return prev.map(function (m, idx) { return (idx === i ? __assign(__assign({}, m), { isFlipping: false }) : m); });
                });
            }, 350);
            markerFlipTimeoutsRef.current.set(i, { swap: swap, end: end });
        });
    };
    // Apply the brush to the side a given marker is currently showing.
    // Sync markerStates length with markerCount. Grow → push default-colored
    // entries; shrink → drop trailing entries (preserves the user's painted
    // colors / flipped states on the markers they keep).
    useEffect(function () {
        setMarkerStates(function (prev) {
            if (prev.length === markerCount)
                return prev;
            if (prev.length < markerCount) {
                var added = Array.from({ length: markerCount - prev.length }, newMarkerState);
                return __spreadArray(__spreadArray([], prev, true), added, true);
            }
            return prev.slice(0, markerCount);
        });
        // Clear stale flip timers for indices that no longer exist.
        if (markerCount < markerFlipTimeoutsRef.current.size) {
            Array.from(markerFlipTimeoutsRef.current.keys()).forEach(function (idx) {
                if (idx >= markerCount) {
                    var t_1 = markerFlipTimeoutsRef.current.get(idx);
                    if (t_1 === null || t_1 === void 0 ? void 0 : t_1.swap)
                        clearTimeout(t_1.swap);
                    if (t_1 === null || t_1 === void 0 ? void 0 : t_1.end)
                        clearTimeout(t_1.end);
                    markerFlipTimeoutsRef.current.delete(idx);
                }
            });
        }
    }, [markerCount]);
    // Bean: front + back colors, plus the "active brush" the user picks from
    // the palette to paint a side. Click a swatch → activeColor; click a bean
    // (front or back) → that side's color is set to activeColor.
    var _q = useState(BEAN_DEFAULT_FRONT_COLOR), beanFrontColor = _q[0], setBeanFrontColor = _q[1];
    var _r = useState(BEAN_DEFAULT_BACK_COLOR), beanBackColor = _r[0], setBeanBackColor = _r[1];
    var _s = useState(GAME_MARKER_PALETTE[0].hex), paletteActiveColor = _s[0], setPaletteActiveColor = _s[1];
    // Which bean face is the paint target when flip is enabled. Clicking a face
    // button selects it; the next swatch click applies directly to that side.
    var _t = useState('front'), beanActiveSide = _t[0], setBeanActiveSide = _t[1];
    // Live region message announced after a color is painted onto a marker.
    // Live region message announced after a marker is flipped.
    var _u = useState(''), flipAnnouncement = _u[0], setFlipAnnouncement = _u[1];
    // Live region message announced when game marker count changes.
    var _v = useState(''), countAnnouncement = _v[0], setCountAnnouncement = _v[1];
    // The count is capped so total instances created on Done never exceed
    // MAX_COUNTERS_PER_CONFIG (10). One slot per non-marker counter; the
    // remainder is the maximum allowable game-marker count.
    var otherSelectedCount = selected.size - (selected.has('gameMarker') ? 1 : 0);
    var maxMarkerCount = Math.max(1, MAX_COUNTERS_PER_CONFIG - otherSelectedCount);
    // Total drawer entries that would be produced: each non-marker type = 1
    // entry; game marker = markerCount entries (one per painted marker).
    var effectiveMarkerCount = selected.has('gameMarker') ? markerCount : 0;
    var totalEntries = effectiveMarkerCount + otherSelectedCount;
    // Once the total entries reach 10, gray-out unchecked tiles. Checked tiles
    // remain interactive so the user can uncheck one to free a slot.
    var isAtMax = totalEntries >= MAX_COUNTERS_PER_CONFIG;
    // C3 — announce the max-reached / max-cleared state to screen readers
    // via a live region so AT users don't silently encounter dimmed checkboxes.
    var _w = useState(''), maxLiveMsg = _w[0], setMaxLiveMsg = _w[1];
    var prevIsAtMaxRef = useRef(false);
    useEffect(function () {
        if (isAtMax && !prevIsAtMaxRef.current) {
            setMaxLiveMsg(t('text.coinsAndCountersMaxReached', { max: MAX_COUNTERS_PER_CONFIG }));
        }
        else if (!isAtMax && prevIsAtMaxRef.current) {
            setMaxLiveMsg(t('text.coinsAndCountersMaxCleared'));
        }
        prevIsAtMaxRef.current = isAtMax;
    }, [isAtMax, t]);
    // Pre-populate from the drawer when the popup opens via Configure.
    // Per spec: "The configuration screen shows all of the counters currently
    // in the right toolbar, even if only one type was selected." So even when
    // Configure was clicked from a single placed counter, the popup reflects
    // the FULL drawer state — the user can add/remove counter types from
    // there. The placed counters on canvas are not affected by Done.
    // Mount-only — Done writes back to the drawer, so depending on it would
    // cause a mid-edit re-hydrate.
    useEffect(function () {
        var _a;
        if (!coinsAndCountersDrawer || coinsAndCountersDrawer.length === 0)
            return;
        var types = new Set();
        var flips = {};
        var markerStatesArr = [];
        coinsAndCountersDrawer.forEach(function (entry) {
            types.add(entry.counterType);
            if (entry.flipEnabled)
                flips[entry.counterType] = true;
            if (entry.counterType === 'gameMarker') {
                markerStatesArr.push({
                    front: entry.color || GAME_MARKER_DEFAULT_COLOR,
                    back: entry.backColor || GAME_MARKER_DEFAULT_COLOR,
                    side: 'front',
                    isFlipping: false,
                });
                if (entry.transparent)
                    setMarkerTransparent(true);
                // Initialize the brush from the first marker's front color so
                // the swatch ring lands on something sensible at mount.
                if (entry.color)
                    setMarkerActiveVariant(entry.color);
            }
            if (entry.counterType === 'bean') {
                if (entry.color) {
                    setBeanFrontColor(entry.color);
                    setPaletteActiveColor(entry.color);
                }
                if (entry.backColor)
                    setBeanBackColor(entry.backColor);
            }
        });
        setSelected(types);
        setFlipMap(flips);
        if (markerStatesArr.length > 0) {
            setMarkerStates(markerStatesArr);
            setMarkerCount(markerStatesArr.length);
        }
        // Choose which type's preview to show in the popup. If Configure was
        // clicked from a placed counter, focus on THAT counter's type so the
        // user sees the section matching what they clicked. Otherwise fall
        // back to the first drawer entry.
        var focusType;
        if (selectedManipulativeId &&
            selectedManipulativeId.startsWith('coinsAndCounters_') &&
            !selectedManipulativeId.endsWith('_dummyForRightTool')) {
            var placed = manipulative.find(function (m) { return m.data.id === selectedManipulativeId; });
            if (placed && placed.data.type === 'coinsAndCounters') {
                focusType = placed.data
                    .counterType;
            }
        }
        if (!focusType)
            focusType = (_a = coinsAndCountersDrawer[0]) === null || _a === void 0 ? void 0 : _a.counterType;
        if (focusType)
            setActiveType(focusType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // C2 — move focus into the dialog on mount so keyboard/AT users land
    // inside the modal, not on the element that triggered it.
    useEffect(function () {
        var _a;
        (_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var toggle = function (id) {
        var adding = false;
        setSelected(function (prev) {
            var next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                // Guard against exceeding 10 total drawer entries. Each non-marker
                // type adds 1 entry; game marker adds markerCount entries.
                var nextEffectiveMarkers = next.has('gameMarker') ? markerCount : 0;
                var nextOtherCount = next.size - (next.has('gameMarker') ? 1 : 0) + (id === 'gameMarker' ? 0 : 1);
                var nextTotal = (id === 'gameMarker' ? markerCount : nextEffectiveMarkers) + nextOtherCount;
                if (nextTotal > MAX_COUNTERS_PER_CONFIG)
                    return prev;
                next.add(id);
                adding = true;
            }
            return next;
        });
        lastToggleWasCheckRef.current = adding;
        setActiveType(id);
    };
    var toggleFlip = function () {
        if (!previewType || !hasTwoSides(previewType))
            return;
        setFlipMap(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[previewType] = !prev[previewType], _a)));
        });
    };
    var onAction = function (type) {
        if (type === 'done' && selected.size > 0) {
            // Build drawer entries (templates) instead of placing counters on the
            // canvas. The user drags each entry from the right-side toolbar onto
            // the canvas to actually create a manipulative instance.
            var entries_1 = [];
            Array.from(selected).forEach(function (counterType) {
                var isMarker = counterType === 'gameMarker';
                var isBean = counterType === 'bean';
                var flipEnabled = !!flipMap[counterType];
                if (isMarker) {
                    // Per-marker entries — each marker keeps its individual front/back
                    // colors so the drawer reflects exactly what the user painted.
                    markerStates.forEach(function (m) {
                        entries_1.push({
                            id: generateId(),
                            counterType: counterType,
                            color: m.front,
                            backColor: m.back,
                            flipEnabled: flipEnabled,
                            transparent: markerTransparent,
                        });
                    });
                }
                else {
                    entries_1.push({
                        id: generateId(),
                        counterType: counterType,
                        color: isBean ? beanFrontColor : undefined,
                        backColor: isBean ? beanBackColor : undefined,
                        flipEnabled: flipEnabled,
                        transparent: false,
                    });
                }
            });
            setCoinsAndCountersDrawer(entries_1);
            // Propagate the new drawer settings to counters ALREADY placed on the
            // canvas. User expectation: if I open Configure, enable Flip on penny,
            // and click Done, my existing pennies on the canvas should also become
            // flippable — not just newly-dragged ones. We update flipEnabled,
            // color, backColor, transparent from the FIRST drawer entry of each
            // type (one entry per type is the common case; for game marker with
            // count > 1 we use the first as canonical). `currentSide` is left
            // alone so users don't lose their flipped state. Counters whose type
            // was removed from the drawer are left untouched on canvas.
            var firstEntryByType_1 = new Map();
            entries_1.forEach(function (entry) {
                if (!firstEntryByType_1.has(entry.counterType)) {
                    firstEntryByType_1.set(entry.counterType, entry);
                }
            });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    if (((_a = item.data) === null || _a === void 0 ? void 0 : _a.type) !== 'coinsAndCounters')
                        return item;
                    var d = item.data;
                    var entry = firstEntryByType_1.get(d.counterType);
                    if (!entry)
                        return item;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, d), { flipEnabled: !!entry.flipEnabled, color: entry.color, backColor: entry.backColor, transparent: !!entry.transparent, 
                            // If Flip is being disabled, snap back to the front side so
                            // a counter doesn't get stuck rendering its back face with
                            // no way to flip it.
                            currentSide: entry.flipEnabled ? d.currentSide : 'front' }) });
                });
            });
            // Mirrors the patternShapes_dummyForRightTool pattern — selecting a
            // dummy id keeps the right-side panel open and tells RightSideToolPanel
            // which toolbar to render.
            setSelectedTool(['coinsAndCounters_dummyForRightTool']);
            // Force the right panel open in case it was previously collapsed.
            customEventHandler.dispatchEvent('openRightDrawer', {});
        }
        else if (type === 'cancel' && coinsAndCountersDrawer.length === 0) {
            // User cancelled and the drawer was empty — close the right panel by
            // clearing the dummy selectedTool. Otherwise the empty drawer sticks
            // around. If the drawer already had content (from a prior Done), we
            // leave it alone so the user keeps their existing setup.
            setSelectedTool([]);
        }
        setSelectedManipulativeId('');
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    // Use the explicit row layout so the empty trailing cells line up with XD.
    var rows = POPUP_ROW_LAYOUT;
    var previewType = activeType && selected.has(activeType)
        ? activeType
        : (_a = Array.from(selected)[selected.size - 1]) !== null && _a !== void 0 ? _a : null;
    var previewMeta = previewType
        ? (_b = COUNTER_TYPES.find(function (ct) { return ct.id === previewType; })) !== null && _b !== void 0 ? _b : null
        : null;
    // Dimensions to draw inside the popup preview area. Counters can opt into
    // a smaller `previewDimensions` when their canvas size would be too big
    // for the popup (e.g. hundredFrame is 550×550 on the stage grid). Falls
    // back to defaultDimensions for everything else.
    var previewDims = (_d = (_c = previewMeta === null || previewMeta === void 0 ? void 0 : previewMeta.previewDimensions) !== null && _c !== void 0 ? _c : previewMeta === null || previewMeta === void 0 ? void 0 : previewMeta.defaultDimensions) !== null && _d !== void 0 ? _d : {
        width: 0,
        height: 0,
    };
    var previewFlipEnabled = previewType ? !!flipMap[previewType] : false;
    var showFlipControl = previewType ? hasTwoSides(previewType) : false;
    var isGameMarkerActive = previewType === 'gameMarker';
    var isBeanActive = previewType === 'bean';
    // Looks up a translated color name from a hex value via the palette map.
    // Click handler for a palette swatch.
    // Swatch click directly applies the color to the counter — no second
    // "click the counter" step required.
    //   • Bean (no flip): swatch → immediately sets front color.
    //   • Bean (flip on): swatch → sets the side currently selected via
    //     beanActiveSide (default front; user clicks a face button to switch).
    //   • Game marker: swatch → applies to every marker's currently visible
    //     side. Individual marker face buttons remain available for per-marker
    //     overrides.
    var onSwatchClick = function (swatch) {
        if (isGameMarkerActive) {
            setMarkerActiveVariant(swatch.id);
            setMarkerStates(function (prev) {
                return prev.map(function (m, idx) {
                    if (idx !== selectedMarkerIndex)
                        return m;
                    var targetSide = previewFlipEnabled ? m.side : 'front';
                    return targetSide === 'back' ? __assign(__assign({}, m), { back: swatch.id }) : __assign(__assign({}, m), { front: swatch.id });
                });
            });
        }
        else if (isBeanActive) {
            setPaletteActiveColor(swatch.hex);
            if (previewFlipEnabled && beanActiveSide === 'back') {
                setBeanBackColor(swatch.hex);
            }
            else {
                setBeanFrontColor(swatch.hex);
            }
        }
    };
    var isSwatchSelected = function (swatch) {
        // Ring follows the active brush (paint-bucket pattern, identical
        // for marker and bean) — independent of any specific marker's
        // currently-displayed side.
        if (isGameMarkerActive)
            return swatch.id === markerActiveVariant;
        if (isBeanActive)
            return paletteActiveColor === swatch.hex;
        return false;
    };
    // Shared focus helper: scrollIntoView before focus so iOS VoiceOver
    // repositions the VO cursor reliably (pattern from NumberFramesManipulativePopup).
    var focusById = function (id) {
        var el = document.getElementById(id);
        if (!el)
            return;
        el.scrollIntoView({ block: 'nearest' });
        el.focus();
    };
    // Auto-focus the first relevant control when the user selects a counter type.
    // 50ms delay lets the controls render and prevents the checkbox-click from
    // stealing focus back immediately.
    // - Game marker: focus the first non-disabled button in the count bar (− or +)
    // - Other flip types: focus the Flip checkbox
    useEffect(function () {
        if (!previewMeta)
            return;
        if (!lastToggleWasCheckRef.current)
            return;
        var showsFlip = showFlipControl || isGameMarkerActive;
        if (!showsFlip)
            return;
        var timer = window.setTimeout(function () {
            if (isGameMarkerActive) {
                var firstBtn = document.querySelector('.ccGameMarkerBar button:not([disabled])');
                if (firstBtn) {
                    firstBtn.scrollIntoView({ block: 'nearest' });
                    firstBtn.focus();
                }
                else {
                    focusById('cc_flip');
                }
            }
            else {
                focusById('cc_flip');
            }
        }, 50);
        return function () { return window.clearTimeout(timer); };
    }, [previewMeta === null || previewMeta === void 0 ? void 0 : previewMeta.id]); // eslint-disable-line react-hooks/exhaustive-deps
    return (_jsxs("div", { ref: dialogRef, tabIndex: -1, className: "coinsAndCountersPopup", role: "dialog", "aria-labelledby": "coinsAndCountersPopupTitle", "aria-modal": "true", children: [_jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", children: maxLiveMsg }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", children: flipAnnouncement }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", children: countAnnouncement }), _jsxs("div", { className: "holder", children: [_jsx(CustomScrollbar, { direction: "vertical", children: _jsxs("div", { className: "ccScrollContent", children: [_jsx("h2", { id: "coinsAndCountersPopupTitle", className: "ccTitle", children: t('text.coinsAndCountersPopupTitle') }), _jsx("div", { className: "ccCounterGrid", role: "group", "aria-label": t('text.coinsAndCountersSelectType'), "aria-describedby": isAtMax ? 'ccMaxHintId' : undefined, children: rows.map(function (row, ri) { return (_jsx("div", { className: "ccCounterGridRow", children: row.map(function (typeId, ci) {
                                            if (!typeId) {
                                                return (_jsx("div", { className: "ccCounterGridCell ccCounterGridCellEmpty" }, "empty-".concat(ri, "-").concat(ci)));
                                            }
                                            var meta = COUNTER_TYPE_BY_ID[typeId];
                                            var isChecked = selected.has(meta.id);
                                            return (_jsx("div", { className: "ccCounterGridCell", children: _jsx(CheckBox, { checked: isChecked, disabled: isAtMax && !isChecked, label: t(meta.labelKey), onClick: function () { return toggle(meta.id); }, ariaLabel: t("text.coinsAndCountersTypeAria_".concat(meta.id), t(meta.labelKey).toLowerCase()) }) }, meta.id));
                                        }) }, "row-".concat(ri))); }) }), isGameMarkerActive ? (
                                /* Combined pill: count + Flip + Transparent (matches XD layout). */
                                _jsx("div", { className: "ccFlipRow", children: _jsxs("div", { className: "ccGameMarkerBar", children: [_jsxs("div", { className: "ccCounterControl", children: [_jsx(ButtonComponent, { type: "imageType4", defaultCSS: "btn4", imgSrc: btnConfig.minus2.imgSrc, clickHandler: function () {
                                                            var next = Math.max(1, markerCount - 1);
                                                            setMarkerCount(next);
                                                            setCountAnnouncement(t('text.coinsAndCountersMarkerCountValue', { count: next }));
                                                        }, disabled: markerCount <= 1, ariaLabel: t('text.coinsAndCountersDecreaseMarkerCount') }), _jsx("div", { className: "ccCountInput", "aria-label": t('text.coinsAndCountersMarkerCount'), children: markerCount }), _jsx(ButtonComponent, { type: "imageType4", defaultCSS: "btn4", imgSrc: btnConfig.plus2.imgSrc, clickHandler: function () {
                                                            var next = Math.min(maxMarkerCount, markerCount + 1);
                                                            setMarkerCount(next);
                                                            setCountAnnouncement(t('text.coinsAndCountersMarkerCountValue', { count: next }));
                                                        }, disabled: markerCount >= maxMarkerCount, ariaLabel: t('text.coinsAndCountersIncreaseMarkerCount') })] }), _jsx("span", { className: "ccBarDivider" }), _jsx("div", { className: "ccBarItem", onClick: forwardClickToCheckbox, children: _jsx(CheckBox, { id: "cc_flip", checked: previewFlipEnabled, label: t('text.coinsAndCountersFlip'), onClick: toggleFlip, ariaLabel: t('text.coinsAndCountersFlip') + ' ' + t('text.coinsAndCountersGameMarker') }) }), _jsx("span", { className: "ccBarDivider" }), _jsx("div", { className: "ccBarItem", onClick: forwardClickToCheckbox, children: _jsx(CheckBox, { id: "cc_transparent", checked: markerTransparent, label: t('text.coinsAndCountersTransparent'), onClick: function () { return setMarkerTransparent(function (v) { return !v; }); }, ariaLabel: t('text.coinsAndCountersTransparent') + ' ' + t('text.coinsAndCountersGameMarker') }) })] }) })) : showFlipControl ? (_jsx("div", { className: "ccFlipRow", children: _jsx("div", { className: "ccFlipPill", onClick: forwardClickToCheckbox, children: _jsx(CheckBox, { id: "cc_flip", checked: previewFlipEnabled, label: t('text.coinsAndCountersFlip'), onClick: toggleFlip, ariaLabel: t('text.coinsAndCountersFlip') +
                                                (previewMeta ? ' ' + t(previewMeta.labelKey).toLowerCase() : '') }) }) })) : null, _jsx("div", { className: "ccPreview".concat(
                                    // All counter types use the vertically-centered layout (XD —
                                    // preview floats between the checkbox area above and whatever
                                    // sits below: palette for bean / game marker, footer for
                                    // standalone counters).
                                    previewMeta ? ' ccPreviewStandalone' : ''), children: previewMeta ? (isBeanActive ? (
                                    /* Bean preview: when flip is on, face buttons select which side
                                       the palette will paint (indicated by a ring). The swatch click
                                       applies directly — no second click on the bean needed. When
                                       flip is off there is only one side, so the preview is purely
                                       decorative and the swatch applies directly. */
                                    previewFlipEnabled ? (_jsxs("div", { className: "ccPreviewPair", children: [_jsx("button", { type: "button", className: "ccBeanPreviewBtn".concat(beanActiveSide === 'front' ? ' ccBeanPreviewBtnActive' : ''), "aria-label": t('text.coinsAndCountersBean') + ' ' + t('text.front') +
                                                    (beanActiveSide === 'front' ? ' ' + t('text.coinsAndCountersActiveSide') : ''), "aria-pressed": beanActiveSide === 'front', onClick: function () { return setBeanActiveSide('front'); }, children: _jsx(CounterGraphic, { counterType: "bean", width: previewDims.width, height: previewDims.height, side: "front", color: beanFrontColor, backColor: beanBackColor }) }), _jsx("button", { type: "button", className: "ccBeanPreviewBtn".concat(beanActiveSide === 'back' ? ' ccBeanPreviewBtnActive' : ''), "aria-label": t('text.coinsAndCountersBean') + ' ' + t('text.back') +
                                                    (beanActiveSide === 'back' ? ' ' + t('text.coinsAndCountersActiveSide') : ''), "aria-pressed": beanActiveSide === 'back', onClick: function () { return setBeanActiveSide('back'); }, children: _jsx(CounterGraphic, { counterType: "bean", width: previewDims.width, height: previewDims.height, side: "back", color: beanFrontColor, backColor: beanBackColor }) })] })) : (
                                    // No flip — swatch applies directly; preview is decorative only.
                                    _jsx("div", { "aria-hidden": "true", children: _jsx(CounterGraphic, { counterType: "bean", width: previewDims.width, height: previewDims.height, side: "front", color: beanFrontColor, backColor: beanBackColor }) }))) : isGameMarkerActive ? (
                                    /* Game marker preview: N markers (one per markerCount), each
                                       with its own flip button below. Per spec, every marker is
                                       independently colored and flipped. Color picker is a
                                       paint-bucket — the swatch sets the brush, then a click on
                                       a marker face paints whichever side that marker is showing.
                                       With Flip OFF the back side is meaningless, so the marker
                                       always renders side="front" and the flip button is disabled. */
                                    _jsx("div", { className: "ccMarkerFlipPreview ccMarkerGrid", children: markerStates.map(function (m, i) {
                                            var showSide = previewFlipEnabled ? m.side : 'front';
                                            var showSideLabel = showSide === 'front' ? t('text.front') : t('text.back');
                                            var markerName = t('text.coinsAndCountersGameMarker') + ' ' + (i + 1) +
                                                (previewFlipEnabled ? ' ' + showSideLabel : '');
                                            return (_jsxs("div", { className: "ccMarkerFlipUnit", children: [_jsx("button", { type: "button", className: "ccBeanPreviewBtn ccMarkerFlipInner".concat(m.isFlipping ? ' ccMarkerFlipping' : '').concat(selectedMarkerIndex === i ? ' ccBeanPreviewBtnActive' : ''), "aria-label": markerName, "aria-pressed": selectedMarkerIndex === i, onClick: function () { return setSelectedMarkerIndex(i); }, children: _jsx(CounterGraphic, { counterType: "gameMarker", width: previewDims.width, height: previewDims.height, side: showSide, color: m.front, backColor: m.back, transparent: markerTransparent }) }), _jsx("button", { type: "button", className: "ccMarkerFlipBtn", "aria-label": !previewFlipEnabled
                                                            ? "".concat(t('text.coinsAndCountersFlipCurrentSide', { name: markerName, side: showSideLabel }), " - ").concat(t('text.coinsAndCountersFlipDisabledHint'))
                                                            : t('text.coinsAndCountersFlipCurrentSide', { name: markerName, side: showSideLabel }), title: t('text.coinsAndCountersFlip'), disabled: !previewFlipEnabled, onClick: function () {
                                                            flipMarker(i);
                                                            var newSideLabel = showSide === 'front' ? t('text.back') : t('text.front');
                                                            window.setTimeout(function () {
                                                                setFlipAnnouncement(t('text.coinsAndCountersFlippedToMarker', {
                                                                    name: markerName,
                                                                    side: newSideLabel,
                                                                }));
                                                            }, 200);
                                                        }, onMouseEnter: function () { return setMarkerFlipBtnHovered(i); }, onMouseLeave: function () { return setMarkerFlipBtnHovered(null); }, onFocus: function () { return setMarkerFlipBtnHovered(i); }, onBlur: function () { return setMarkerFlipBtnHovered(null); }, children: _jsx("img", { src: "./assets/images/manipulative/coinsAndCounters/".concat(previewFlipEnabled && markerFlipBtnHovered === i
                                                                ? 'flipH'
                                                                : 'flipN', ".svg"), alt: "", draggable: false }) })] }, i));
                                        }) })) : previewFlipEnabled && hasTwoSides(previewMeta.id) ? (_jsxs("div", { className: "ccPreviewPair", "aria-hidden": "true", children: [_jsx(CounterGraphic, { counterType: previewMeta.id, width: previewDims.width, height: previewDims.height, side: "front" }), _jsx(CounterGraphic, { counterType: previewMeta.id, width: previewDims.width, height: previewDims.height, side: "back" })] })) : (
                                    // H4 — standalone decorative preview is hidden from AT.
                                    _jsx("div", { "aria-hidden": "true", children: _jsx(CounterGraphic, { counterType: previewMeta.id, width: previewDims.width, height: previewDims.height, side: "front" }) }))) : null }), (isGameMarkerActive || isBeanActive) && (
                                // M1 — aria-label is now type-specific (bean vs. game marker).
                                // M2 — swatch aria-labels use translated color names via colorLabelKey.
                                _jsx("div", { className: "ccPaletteRow", role: "group", "aria-label": (isGameMarkerActive
                                        ? t('text.coinsAndCountersGameMarker')
                                        : t('text.coinsAndCountersBean')) +
                                        ' ' +
                                        t('text.colorPalette'), children: GAME_MARKER_PALETTE.map(function (swatch) { return (_jsx("button", { type: "button", className: "ccPaletteSwatch ".concat(isSwatchSelected(swatch) ? 'ccPaletteSwatchSelected' : ''), style: { background: swatch.hex }, "aria-label": t(swatch.colorLabelKey), "aria-pressed": isSwatchSelected(swatch), onClick: function () { return onSwatchClick(swatch); } }, swatch.id)); }) })), previewMeta && (_jsx("div", { style: { position: 'relative', height: 0, overflow: 'visible' }, children: _jsx("button", { type: "button", className: "ccReturnToSelectionBtn", "aria-label": t('text.coinsAndCountersReturnToSelection', {
                                            type: t(previewMeta.labelKey).toLowerCase(),
                                        }), onClick: function () { return focusById("cc_".concat(previewMeta.id)); } }) }))] }) }), _jsxs("div", { className: "ccFooter", children: [_jsx(FooterActionsButtons, { onActionBtnClick: onAction, disableDoneBtn: selected.size === 0 }), _jsx("span", { id: "ccMaxHintId", className: "ccMaxHint", children: t('text.coinsAndCountersMaxHint', { max: MAX_COUNTERS_PER_CONFIG }) })] })] })] }));
};
export default CoinsAndCountersManipulativePopup;
